// @flow
import './TreeAssetView.css';

import { EventEmitter } from 'events';
import * as React from 'react';
import { DropTarget } from 'react-dnd';
import _ from 'lodash';
import StoreListener from '../../store/StoreListener';
import { PWD, BREADCRUMB_ASSETS, LOADED_TREE_ITEMS, GLOBAL_SEARCH_INFO } from '../../store/Constants';
import { RepoItem } from '../../autogenerated/ZoomBridge';
import TreeView, { AssetTreeNode } from '../../common/TreeView';
import ViewUtil from '../../ViewUtil';
import AssetListCache, { foldersOnly } from '../../queries/asset/AssetListCache';
import FileInfoUtil from '../../util/FileInfoUtil';
import { createDndConfig } from '../../dnd/reactDndConfig';
import Store from '../../store/Store';
import StringUtil from '../../util/StringUtil';
import t from '../../i18n/I18n';
import AllFilesTree from './AllFilesTree/AllFilesTree';
import { ThemeSetup } from '../../themes/ThemeSetup';
import RemoteFileStatusHandler from '../../queries/status/RemoteFileStatusHandler';
import withUniResizableColumn from '../../hoc/withUniReziableColumn';
import { getFolderIcon } from '../../util/FolderStatusUtil';
import { SetterStoreUtil } from '../../store/setterStoreUtil';

const debug = require('../../debug/debug')('wc:assets:TreeAssetView');

export const treeEmitter = new EventEmitter();

const TREE_NAMESPACE = 'TREEVIEW.TreeAssetView';

const getAsset = (props: any) => props.node.getRepoItem();
const dnd = createDndConfig(getAsset);

type Props = {
  assets: Array<RepoItem>,
  PWD: number[],
  BREADCRUMB_ASSETS: Array<RepoItem>,
  isIndependentTreeIns: Boolean,
  onRefreshPWD: Function
};

/* type State = {
  root: ?TreeRootNode,
  selectedAsset: ?RepoItem,
  rootNode: AssetTreeNode
}; */

type State = {
  // root: ?TreeRootNode,
  selectedAsset: ?RepoItem,
  root: AssetTreeNode
};

const storeItems = [PWD, BREADCRUMB_ASSETS];
//$FlowFixMe
@StoreListener(storeItems)
class TreeAssetView extends React.Component<Props, State> {
  _assetCache: AssetListCache = AssetListCache.getInstance();
  _ignoreNextBreadcrumbChange = false;
  _didWait = false;
  _treeView: TreeView;
  ROOT_FUID: number = 0;
  forceUpdateTimer = undefined;

  constructor(props: Props) {
    super(props);

    this.state = {
      root: this.getNewRootNode(),
      selectedAsset: null
    };

    // If we're opening the root asset tab view (i.e. not by clicking the back
    // button which should preserve tree view state) then reset tree view state.
    if (!props.PWD.length && Store.get(TREE_NAMESPACE)) {
      debug('[constructor()] clearing tree view');
      Store.put(TREE_NAMESPACE, null);
    }
  }

  componentWillMount() {
    const root = Store.get(LOADED_TREE_ITEMS);
    if (root) {
      this.state.root = root;
      Store.put(LOADED_TREE_ITEMS, null);
    }
  }

  componentDidMount() {
    this._isMounted = true;
    treeEmitter.addListener('refreshNodes', this.refreshNodes);
    treeEmitter.addListener('updateCurrentActiveNode', this.updateCurrentActiveNode);
    this.buildTree();
    setTimeout(() => {
      if (this.props.BREADCRUMB_ASSETS.length === 0) {
        this.refreshNodes();
      }
    }, 1000);
    if (this.props.isIndependentTreeIns) {
      this.updateActiveNode();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const assets = this.props.BREADCRUMB_ASSETS;
    if (assets !== prevProps.BREADCRUMB_ASSETS) {
      if (this._ignoreNextBreadcrumbChange) {
        this._ignoreNextBreadcrumbChange = false;
      } else {
        this.updateActiveNode();
      }
    }
  }

  componentWillUnmount() {
    const { root } = this.state;
    Store.put(LOADED_TREE_ITEMS, root);
    this._isMounted = false;
    treeEmitter.removeListener('refreshNodes', this.refreshNodes);
    treeEmitter.removeListener('updateCurrentActiveNode', this.updateCurrentActiveNode);

    // Store.put(BREADCRUMB_ASSETS, []);
  }

  forceUpdateTimerCall = () => {
    if (this.forceUpdateTimer) {
      clearTimeout(this.forceUpdateTimer);
    }
    this.forceUpdateTimer = setTimeout(() => {
      if (this._isMounted) {
        this.forceUpdate();
      }
    }, 500);
  };

  updateCurrentActiveNode = () => {
    if (this._treeView && this._treeView.state.activeNode) this._treeView.state.activeNode.reloadCurrentLoad();
  };

  reloadRootProjectChild = () => {
    if (this.state.root) {
      const isNoWCStatus = this.state.root.getChildren().find((e) => e.getRepoItem().wcStatusObject === undefined);
      if (isNoWCStatus) {
        const items = this.state.root.getChildren().map((e) => e.getRepoItem());
        RemoteFileStatusHandler.getInstance()
          .resetAndSubmitFile(items, 0)
          .then(
            () => {
              items.forEach((e) => {
                const child = this.state.root.getChildren().find((s) => s.getFuid() === e.getFuid());
                child.getRepoItem().wcStatusObject = e.wcStatusObject;
              });
            },
            () => {}
          );
      }
    }
  };

  refreshNodes = (fuids: number[]) => {
    debug('[refreshNodes()] fuids: ', fuids);
    const expandedNodeIds = this._treeView ? this._treeView.state.expandedNodes : undefined;

    const visit = async (node: AssetTreeNode) => {
      if (
        _.includes(fuids, node.getFuid()) ||
        (node.getFuid() > 0 && expandedNodeIds && !!expandedNodeIds[node.getFuid()])
      ) {
        debug('[refreshNodes()] calling fetchChildren() on fuid: ', node.getFuid());
        await node.remoteFetch(true);
        this.forceUpdateTimerCall();
      }

      if (node.hasChildren()) {
        node.getChildren().forEach(visit);
      }
    };

    visit(this.state.root);
    this.forceUpdateTimerCall();
  };

  visitFolder = async (node, fuids, expandedNodeIds, manualRefresh) => {
    if (
      _.includes(fuids, node.getFuid()) ||
      (node.getFuid() > 0 && expandedNodeIds && !!expandedNodeIds[node.getFuid()])
    ) {
      debug('[refreshNodes()] calling fetchChildren() on fuid: ', node.getFuid());
      await node.remoteFetch(manualRefresh);
    }

    if (node.hasChildren()) {
      node.getChildren().forEach((n) => this.visitFolder(n, fuids, expandedNodeIds, true));
    }
    return;
  };

  getExpendedFuids() {
    const assets = [];
    this.props.currentExpends.forEach((e) => {
      if (e > 0) assets.push(new RepoItem().setFuid(Number(e)));
    });
    return assets;
  }

  updateActiveNode = async (attempt: number = 1) => {
    if (!this._treeView && attempt <= 5) {
      debug('[updateActiveNode()] treeView not ready, trying again in 1s');
      return setTimeout(() => this.updateActiveNode(attempt + 1), 1000);
    }

    const assets = this.props.isIndependentTreeIns ? this.getExpendedFuids() : this.props.BREADCRUMB_ASSETS;
    const { root } = this.state;
    let node: AssetTreeNode = root;
    let i = 0;
    const expandedNodeIdsDefined = this._treeView ? this._treeView.state.expandedNodes : undefined;
    const visit = async () => {
      const asset = assets[i];
      debug('[updateActiveNode() visit()] data: ', { node, asset, i });

      if (!asset) {
        return;
      }

      if (node.isFolder() && !node.isChildLoaded()) {
        await node.remoteFetch();
      }

      if (window.gs.currentNode && node.getFuid() === window.gs.currentNode) {
        await node.remoteFetch();
        window.gs.currentNode = null;
      }
      const child = node.getChildren().find((n: AssetTreeNode) => n.getFuid() === asset.getFuid());
      if (!child) {
        debug('[updateActiveNode() visit()] did not find node in children: ', node.getChildren());
        return;
      }

      node = child;
      i++;
      if (i >= assets.length) {
        debug('[updateActiveNode() visit()] reached the end');
        await this.visitFolder(node, [node.getFuid()], expandedNodeIdsDefined, false);
        //  if (node.isFolder()) {
        //    await node.remoteFetch();
        //  }
        return;
      }

      return await visit();
    };
    debug('[updateActiveNode() visit()] reached the end');
    await visit();

    if (!this._treeView) {
      // Lots of async. Component may have unmounted.
      return;
    }
    debug('[updateActiveNode()] PWD: %o ViewUtil.getPwd(%O)', assets, ViewUtil.getPwd());
    const expandedNodeIds = assets.map((a) => a.getFuid());

    this._treeView.updateActiveNodeAndExpandedNodes(node, expandedNodeIds);
  };

  getNewRootNode(): AssetTreeNode {
    return new AssetTreeNode(new RepoItem().setFuid(0).setName(t('ASSETS')), null);
  }

  buildTree = async () => {
    debug('[buildTree()] begin');
    const { root } = this.state;
    if (root.isChildLoaded()) {
      return;
    }
    try {
      const rootNode: AssetTreeNode = this.getNewRootNode();
      await rootNode.remoteFetch();
      if (debug.enabled) {
        debug(
          '[buildTree()] Project Fuids: %o',
          rootNode.getChildren().map((c) => c.getFuid())
        );
        debug('[buildTree()] Project RepoItems: %O', rootNode.getChildren());
      }
      //rootNode.setChildrenFromRepoItem(projectRepoItems);
      /*  const root: TreeRootNode = {
         __hide: true,
         id: -1,
         fetchChildren: _.noop
       };
       root.children = projectRepoItems.map((project: RepoItem) => {
         return this.visit(project, root);
       }); */

      debug('[buildTree()] done. root: %O', rootNode);
      //this.setState({ root });
      if (this._isMounted) {
        this.setState({ root: rootNode });
      }
      this.updateActiveNode();
      //  return;
    } catch (e) {
      debug(e);
      //   return;
    } finally {
      //this.reloadRootProjectChild();
    }
    /*  let projectFuids = this._assetCache.fuid2Kids.get(this.ROOT_FUID);
      let projects;
      if (projectFuids) {
        projects = _.compact(projectFuids.map((fuid) => this._assetCache.getKid(0, fuid)));
        if (projects.length < projectFuids.length) {
          projects = await this._assetCache.getOrFetchRepoItems(0, projectFuids);
        }
      } else {
        if (!this._didWait) {
          debug('[buildTree()] no projectFuids, trying again in 1s');
          this._didWait = true;
          setTimeout(this.buildTree, 1000);
          return;
        }
  
        debug('[buildTree()] fetching projects');
        projectFuids = await this._assetCache.fetchFuids(0);
        projects = await this._assetCache.getOrFetchRepoItems(0, projectFuids);
      }*/
  };

  getRemotePath = (node: AssetTreeNode) => {
    if (!(node && node.getRepoItem())) {
      return null;
    }

    let n = node;
    const paths = [];
    while (n) {
      const asset: RepoItem = n.getRemotePath();
      if (!asset) {
        break;
      }

      paths.push(asset.getName());
      n = n.getParent();
    }

    return '/' + paths.reverse().join('/');
  };

  visit = (asset: RepoItem, parent: AssetTreeNode): AssetTreeNode => {
    const id = asset.getFuid();

    const node: AssetTreeNode = new AssetTreeNode(asset, parent);

    if (!asset.getRemotePath()) {
      asset.setRemotePath(this.getRemotePath(node));
    }

    /* if (FileInfoUtil.isFolder(asset)) {
      node.children = [];
      node.fetchChildren = () => this.fetchFuids(node);
    } */

    return node;
  };

  fetchFuids = async (node: AssetTreeNode) => {
    try {
      setTimeout(() => {
        node.loading = true;
        if (this._isMounted) {
          this.forceUpdate();
        }
      }, 10);

      const fuids = await this._assetCache.fetchFuids(node.id, {
        fileFilters: foldersOnly,
        force: true,
        nocache: true //nocache because some case it cache fuids for display assets.
      });
      const folders = await this._assetCache.getOrFetchRepoItems(node.id, fuids);

      debug('[fetchFuids()] data: ', { fuids, folders });

      node.children = folders
        .sort(StringUtil.createSortFn('_name'))
        .map((childAsset: RepoItem) => this.visit(childAsset, node));

      setTimeout(() => {
        node.loading = false;
        if (this._isMounted) {
          this.forceUpdate();
        }
      }, 50);
    } catch (err) {
      debug('[fetchFuids()] error: ', err, 'node: ', node);
    }
  };

  onSelect = (node: AssetTreeNode) => {
    const asset: RepoItem = node.getRepoItem();
    SetterStoreUtil.deactivateGlobalSearch();
    if (FileInfoUtil.isFolder(asset)) {
      let n = node;
      let pwd = [];
      while (n && !n.isHide()) {
        pwd.push(n.getFuid());
        n = n.getParent();
      }

      pwd = pwd.reverse();
      if(pwd.length > 1) {
        pwd.splice(0,1);
      }
      if (_.isEqual(pwd, ViewUtil.getPwd())) {
        debug('[onSelect()] no change in pwd, return.');
        return;
      }
      if (this.props.onselectedPath) {
        //const projectPath = this._assetCache.getProjectPath(asset.getProject());
        this.props.onselectedPath(asset.getRemotePath(), asset, pwd);
        return;
      }

      debug('[onSelect()] folder selected. Setting pwd: ', pwd);
      this._ignoreNextBreadcrumbChange = true;
      ViewUtil.setPwd(pwd);
    } else {
      debug('[onSelect()] asset selected. Navigating to detail view.');
      ViewUtil.switchToAssetDetailView(asset.getFuid());
    }
  };

  getIcon = (node: AssetTreeNode, isExpand: boolean = false, isActive: boolean = false) => {
    return getFolderIcon(node.getRepoItem(), isExpand, isActive);
    /*return isActive
      ? 'images/folder_open_active.svg'
      : isExpand
      ? ThemeSetup.getImageURL('folder_open_expand.svg')
      : ThemeSetup.getImageURL('folder_gray.svg');*/
    //return isExpand ? 'images/folder-opened-black.svg' : 'images/folder_gray.svg'; //Todo - Different Image used for opened folder, so commented to remove later
  };

  getLabel = (node: AssetTreeNode) => {
    return node.getRepoItem().getName();
  };

  getIsLeaf = (node: AssetTreeNode) => {
    return !FileInfoUtil.isFolder(node.getRepoItem());
  };

  decorate = (Component: any) => {
    return DropTarget('ASSET', dnd.target.spec, dnd.target.collect)(Component);
  };

  render() {
    const { root } = this.state;
    const { currentExpends, onRefreshPWD, widthOfCol, onToggleClick, hideSeperator } = this.props;
    if (!root) {
      return null;
    }
    const treeStyle = {
      paddingTop : this.props.isIndependentTreeIns ? null : 15,
      width: `${widthOfCol}px`
    }

    if(!hideSeperator) {
      treeStyle.height = 'calc(100vh - 70px)'
    }
    
    return (
      <div name="TreeAssetView"  className='flex-col' style={treeStyle}>
        <div style={{ minHeight: '30px', fontSize: '12px', paddingLeft: '16px', color: 'var(--tree-node-font-color)' }}>
              <span>ASSETS</span>
              {!hideSeperator ? <img 
                style={{ width: '16px', height: '16px', float: 'right', position: 'relative', right: '10px', transform: 'rotateY(180deg)', cursor: 'pointer' }}
                src='images/icons_expand_left_menu.svg'
                onClick={onToggleClick}
              />: null}
            </div>
        {this.props.isIndependentTreeIns ? null : <AllFilesTree />}
        {this.props.isIndependentTreeIns ? null : <div className="folder">
          <span><img src={ThemeSetup.getImageURL('refactor/icons_folder.svg')} className='folderIcon' /></span>
          {t('BROWSE')}</div>}
        <TreeView 
          showTreeView ={this.props.showTreeView}
          ref={(ref) => (this._treeView = ref)}
          namespace="TreeAssetView"
          root={root}
          onRefreshPWD= {onRefreshPWD}
          currentExpends={currentExpends}
          onSelect={this.onSelect}
          getIcon={this.getIcon}
          getLabel={this.getLabel}
          getIsLeaf={this.getIsLeaf}
          decorate={this.decorate}
          isIndependentTreeIns={this.props.isIndependentTreeIns}
        />
      </div>
    );
  }
}

export default withUniResizableColumn(TreeAssetView);